import PublicListingsCard from 'components/public-listing-card/public-listing-card';
import { useMarketplaceConfig } from 'hooks/use-marketplace-config';
import { Session } from 'next-auth';
import { useSession } from 'next-auth/react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { useMedia } from 'react-use';
import styled, { css } from 'styled-components';
import { useApiReady } from '../../hooks/use-api-ready';
import { useDialogToggle } from '../../hooks/use-dialog-toggle';
import { useMarketplacePublicListing } from '../../hooks/use-marketplace-public-listing';
import {
  ListingBookmarkTypeEnum,
  ListingRead,
  PaginatedListingReadList,
  States,
} from '../../lib/api/generated';
import { useStore } from '../../lib/store';
import MarketplaceTableView from '../../pages/marketplace/components/marketplace-table-view';
import { PATHS, REDIRECT_URL } from '../../settings/config';
import { breakpoints } from '../../styles/breakpoints';
import { globalTheme } from '../../styles/global-theme';
import AgoraAdvertisement from '../agora-advertisement/agora-advertisement';
import CreateListingDialog from '../create-listing-dialog/create-listing-dialog';
import MarketplaceFooter from '../footer/footer';
import MarketplaceBannerGif from '../images/marketplace-banner.gif';
import MarketplaceFilterCard from '../marketplace-filter-card/marketplace-filter-card';
import { Button, Flex, Heading, Icon, Loader, Text, Toggle } from '../ui';
import { MarketplaceFilterCardEnum } from './filters/helpers';

const ListingWrapper = styled.div<{ session: Session | null }>`
  position: relative;
  background-color: ${globalTheme.colors.white};
  min-height: 100vh;
`;

const ListingsCardContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(370px, 1fr));
  align-items: center;
  justify-content: center;
  gap: ${globalTheme.space[2]};
  flex-wrap: wrap;
  width: 100%;
  justify-items: center;

  @media screen and (max-width: ${breakpoints.small}) {
    grid-template-columns: 1fr;
  }
`;

const ListWrapper = styled.div<{ tableView?: boolean; dealRoom?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin: auto;
  margin: 0 auto;
  padding: ${globalTheme.space[5]};

  ${(props: { tableView?: boolean; dealRoom?: boolean }) =>
    props.dealRoom &&
    css`
      padding-bottom: ${props.tableView
        ? globalTheme.space[2]
        : globalTheme.space[5]};
    `};
  @media screen and (max-width: ${breakpoints.medium}) {
    padding: ${globalTheme.space[1]};
  }

  @media screen and (max-width: ${breakpoints.small}) {
    .status-filter {
      border: 1px solid ${globalTheme.colors.darkBlue};
      border-radius: ${globalTheme.space[0]};
      padding: ${globalTheme.space[0]} 0;
    }
  }
`;

const LoadMoreBtn = styled.div`
  max-width: max-content;
  margin: auto;
  margin: ${globalTheme.space[3]};
`;

const StyledLoader = styled(Loader)`
  height: calc(100vh - 190px - 64px);
  padding: 30vh 45vw;
`;

const Header = styled.div<{ isNavOpen?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: ${globalTheme.space[1]};

  @media screen and (max-width: 1300px) {
    ${({ isNavOpen }) =>
      isNavOpen &&
      css`
        flex-direction: column;
        align-items: flex-start;
        gap: ${globalTheme.space[2]};
        justify-content: flex-start;
      `};
  }
  @media screen and (max-width: 1070px) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${globalTheme.space[2]};
    justify-content: flex-start;
  }

  @media screen and (max-width: ${breakpoints.small}) {
    flex-direction: column;
    justify-content: flex-start;

    > button {
      align-self: flex-start;
      margin-top: ${globalTheme.space[2]};
    }
  }
`;

const NoResult = styled(Text)`
  padding-bottom: 60px;
  text-align: center;
`;

const TableViewWrapper = styled.div`
  display: flex;
  gap: ${globalTheme.space[0]};
  align-self: flex-start;
  margin-left: ${globalTheme.space[1]};
  @media screen and (max-width: ${breakpoints.medium}) {
    margin: ${globalTheme.space[1]} 0;
  }
`;

const LeftContent = styled.div`
  display: flex;
  gap: ${globalTheme.space[1]};
  @media screen and (max-width: ${breakpoints.small}) {
    justify-content: flex-start;
    width: 100%;
    flex-direction: column;
  }
`;

const ToggleContainer = styled.div`
  padding-left: ${globalTheme.space[5]};
  padding-right: ${globalTheme.space[5]};
  padding-bottom: ${globalTheme.space[0]};
  display: flex;
  flex-direction: column;
  width: max-content;
  @media screen and (max-width: ${breakpoints.medium}) {
    padding: ${globalTheme.space[1]};
  }
`;

const ActionButton = styled.div`
  display: flex;
  gap: ${globalTheme.space[0]};

  .sell-listing-btn,
  .buy-listing-btn {
    background-color: ${globalTheme.colors.purple};
  }
  @media screen and (max-width: ${breakpoints.small}) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
    justify-content: space-between;
    width: 100%;
  }
`;

const MobileFilterWrapper = styled.div`
  display: none;
  @media screen and (max-width: ${breakpoints.small}) {
    display: flex;
  }
`;

const Spacer = styled.div`
  width: 100%;
  margin-top: ${globalTheme.space[1]};
`;

const FilterWrapper = styled.div`
  width: 100%;
  @media screen and (max-width: ${breakpoints.small}) {
    display: none;
  }
`;

const StatusContainer = styled(Flex)`
  @media screen and (max-width: ${breakpoints.small}) {
    flex-wrap: wrap;
  }
`;

const TestDiv = styled.div`
  width: 100%;
  height: 100px;
  padding: 2px;
  background-color: #f0f0f0;
  margin-bottom: ${globalTheme.space[2]};
`;

const BuyListingNoticeWrapper = styled.div<{ hide: boolean }>`
  background-color: ${globalTheme.colors.lightPink};
  padding: ${globalTheme.space[0]} ${globalTheme.space[2]};
  border-radius: 20px;
  margin-bottom: ${globalTheme.space[1]};
  padding-right: ${globalTheme.space[5]};
  justify-content: center;
  gap: ${globalTheme.space[1]};
  align-items: center;
  position: relative;
  display: ${({ hide }) => (hide ? 'none' : 'flex')};

  > h3 {
    width: unset;
  }
  .image-wrapper {
    width: 100%;
    max-width: 800px;
    height: 130px;
    border-radius: ${globalTheme.space[0]};
  }
  @media screen and (max-width: ${breakpoints.small}) {
    flex-direction: column;
    gap: ${globalTheme.space[0]};
    padding: ${globalTheme.space[1]} ${globalTheme.space[2]};
    .image-wrapper {
      width: 100%;
    }
  }
`;

const BuyOrderCloseWrapper = styled.div`
  position: absolute;
  right: ${globalTheme.space[1]};
  top: ${globalTheme.space[1]};
  &:hover {
    cursor: pointer;
  }
`;

export const MarketplaceListings = ({
  route,
  openNotificationDialog,
  hideFooter,
  forceQueryEnabled,
}: {
  route: string;
  openNotificationDialog?: VoidFunction;
  hideFooter?: boolean;
  /** Enable the listings query on routes outside the marketplace index pages
   * (e.g. the gone-listing view rendered at /marketplace/listing/[id]). */
  forceQueryEnabled?: boolean;
}) => {
  const router = useRouter();
  const [tableView, setTableView] = useState<boolean>(false);
  const { data: session, status: sessionStatus } = useSession();
  const apiReady = useApiReady();
  const sessionResolved = sessionStatus !== 'loading';
  const [defaultSoldValue, setDefaultSoldValue] = useState<boolean>(false);
  const isMobile = useMedia(`(max-width: ${breakpoints.medium})`, false);
  const isNavOpen = useStore((store) => store.isNavOpen);
  const queryLength = Object.keys(router.query).length;
  const [hideBuyNotice, setHideBuyNotice] = useState<boolean>(false);

  // https://livestockpricing.atlassian.net/browse/LPD-3533
  // This is the deal room variable set on the .env-cmdrc file. Once The deal room is ready for the specific environments.
  // Update the variable for the specific environment. When the deal room is ready for prod. Delete this build variable and fix the conditions accordingly in the code below.

  useEffect(() => {
    return () => {
      //removed the iframe generated by script
      removeIframe();
    };
  }, [apiReady]);

  useEffect(() => {
    const storedTableView = localStorage.getItem('TABLE_VIEW');
    if (storedTableView !== null) {
      setTableView(storedTableView === 'true');
    }
  }, []);

  useEffect(() => {
    const hideBuyNotice = localStorage.getItem('HIDE_BUY_NOTICE');
    if (hideBuyNotice === 'true') {
      setHideBuyNotice(true);
    }
  }, [hideBuyNotice]);

  useEffect(() => {
    if (router.query.sold) {
      setDefaultSoldValue(
        typeof router.query.sold === 'string' && router.query.sold === 'true'
          ? true
          : false
      );
    } else {
      setDefaultSoldValue(false);
    }
  }, [router.query]);

  const removeIframe = () => {
    const iframeElement = document.getElementById('LeadboosterContainer');
    if (iframeElement) {
      iframeElement.remove();
    }
  };

  const handleCreateListing = () => {
    if (!session || session === null) {
      openCreateListingDialog();
    } else {
      localStorage.setItem(REDIRECT_URL, router.pathname);
      router.push(PATHS.marketplace.createListing);
    }
  };

  const handleGoToMyBuyListings = (route: string): Promise<Boolean> => {
    if (route.includes('app')) {
      return router.push(PATHS.app.myBuyListings);
    } else {
      return router.push(route);
    }
  };

  const handleGoToMySellListings = (route: string): Promise<Boolean> => {
    if (route.includes('app')) {
      return router.push(PATHS.app.mySellListings);
    } else {
      return router.push(route);
    }
  };

  const handleTableView = () => {
    const getTableView = localStorage.getItem('TABLE_VIEW');
    const newTableView = getTableView !== 'true';
    localStorage.setItem('TABLE_VIEW', String(newTableView));
    setTableView(newTableView);
  };

  const hideBuyOrderNoticeHandler = (): void => {
    localStorage.setItem('HIDE_BUY_NOTICE', 'true');
    setHideBuyNotice(true);
  };

  const listingsQueryParams = ({
    page,
    pageSize,
    sold,
    auction,
    product,
    state,
    search,
    priceType,
    priceMax,
    priceMin,
    quantityMax,
    quantityMin,
    type,
    stockCat,
    networks,
    apiKey,
    bookmark,
    org,
  }: {
    page?: number;
    pageSize?: number;
    sold?: string;
    auction?: string;
    product?: 'CT' | 'GT' | 'SH';
    state?: States;
    search?: string;
    priceType?: string;
    quantityMax?: string;
    quantityMin?: string;
    priceMax?: string;
    priceMin?: string;
    networks?: string;
    type?: 'BUY' | 'SELL';
    stockCat?: string;
    apiKey: boolean;
    bookmark?: ListingBookmarkTypeEnum;
    org?: string;
  }) => {
    return {
      page,
      pageSize,
      sold,
      auction,
      product,
      state,
      search,
      priceType,
      priceMax,
      priceMin,
      quantityMax,
      quantityMin,
      type,
      stockCat,
      networks,
      apiKey,
      bookmark,
      org,
    };
  };
  // Check we're still on a marketplace index page (not navigating away during exit animation)
  const isOnMarketplacePage =
    router.pathname === '/' ||
    router.pathname === '/marketplace' ||
    router.pathname === '/app/marketplace';

  const isQueryEnabled =
    router.isReady &&
    (isOnMarketplacePage || !!forceQueryEnabled) &&
    sessionResolved &&
    (apiReady || sessionStatus === 'unauthenticated');

  const {
    data,
    isLoading: queryLoading,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
    hasNextPage,
    isRefetching,
    refetch,
  } = useMarketplacePublicListing({
    ...listingsQueryParams({ ...router.query, apiKey: apiReady }),
    pageSize: tableView ? 100 : undefined,
    enabled: isQueryEnabled,
  });

  // Treat "query not yet enabled" as loading to match server-side render and avoid hydration error
  const listingsLoading = queryLoading || !isQueryEnabled;

  const { data: marketplaceConfig, isLoading: isMarketplaceConfigLoading } =
    useMarketplaceConfig();

  const listingData = useMemo(() => {
    let dataToReturn: any = [];
    data &&
      data.pages.map((listing: PaginatedListingReadList) => {
        dataToReturn = [...dataToReturn, ...listing.results];
      });

    return dataToReturn;
  }, [data]);

  const [
    createListingDialogOpen,
    openCreateListingDialog,
    closeCreateListingDialog,
  ] = useDialogToggle(false);

  return (
    <ListingWrapper session={session}>
      {listingsLoading && <StyledLoader size={4} />}

      {!listingsLoading && (
        <>
          <ListWrapper tableView={tableView}>
            <Header isNavOpen={isNavOpen}>
              <LeftContent>
                <div>
                  <Heading level='h2' as='h1'>
                    Marketplace
                  </Heading>
                </div>
                <div>
                  {isMobile && (
                    <Flex justifyContent='space-between' alignItems='center'>
                      <TableViewWrapper>
                        <Toggle
                          onChange={handleTableView}
                          checked={tableView}
                          toggleOnContent={'Deal room'}
                          toggleOffContent={'Deal room'}
                        />
                      </TableViewWrapper>
                      <MobileFilterWrapper>
                        <MarketplaceFilterCard
                          instance={MarketplaceFilterCardEnum.MARKETPLACE}
                          route={route}
                        />
                      </MobileFilterWrapper>
                    </Flex>
                  )}
                  {!isMobile && (
                    <>
                      <Toggle
                        onChange={handleTableView}
                        checked={tableView}
                        toggleOnContent={'Deal room'}
                        toggleOffContent={'Deal room'}
                      />
                    </>
                  )}
                </div>
              </LeftContent>
              <ActionButton>
                <Button
                  size='small'
                  className='create-listing-btn smallScreen-addBooking'
                  onClick={handleCreateListing}
                >
                  Create listing
                </Button>
                {session && (
                  <Button
                    size='small'
                    variant='outline'
                    className='smallScreen-addNotification'
                    onClick={() => {
                      openNotificationDialog?.();
                    }}
                  >
                    Manage alerts
                    {isMarketplaceConfigLoading && <Loader size={1} />}
                    {!isMarketplaceConfigLoading &&
                    marketplaceConfig?.alerts?.user?.count
                      ? ` (${marketplaceConfig?.alerts?.user?.count})`
                      : null}
                  </Button>
                )}
                {session && (
                  <>
                    <Button
                      size='small'
                      className='sell-listing-btn smallScreen-myListing'
                      onClick={() =>
                        handleGoToMyBuyListings(PATHS.marketplace.buyListings)
                      }
                    >
                      My Buy Listings
                    </Button>
                    <Button
                      size='small'
                      className='sell-listing-btn smallScreen-myListing'
                      onClick={() =>
                        handleGoToMySellListings(PATHS.marketplace.sellListings)
                      }
                    >
                      My Sell Listings
                    </Button>
                  </>
                )}
              </ActionButton>
            </Header>
            {!tableView && (
              <>
                <FilterWrapper>
                  <MarketplaceFilterCard
                    instance={MarketplaceFilterCardEnum.MARKETPLACE}
                    route={route}
                  />
                </FilterWrapper>
                <Spacer>
                  <BuyListingNoticeWrapper hide={hideBuyNotice}>
                    <Icon component='AgoraLogo' size={4} dontFill />
                    <Flex
                      alignItems='center'
                      stackOnMedium
                      justifyContent='center'
                      gap={1}
                    >
                      <Text
                        as={'span'}
                        textAlign='center'
                        maxWidth={'max-content'}
                        fontSize={2}
                      >
                        Get instant alerts for new listings and saleyard
                        reports.
                        <br />
                        Download the FREE Agora Livestock Markets app on{' '}
                        <a
                          href='https://agoralivestock.app.link/marketplaceapplink'
                          target='_blank'
                          rel='noopener noreferrer'
                          style={{ color: globalTheme.colors.blue }}
                        >
                          App Store
                        </a>{' '}
                        or{' '}
                        <a
                          href='https://agoralivestock.app.link/marketplaceapplink'
                          target='_blank'
                          rel='noopener noreferrer'
                          style={{ color: globalTheme.colors.blue }}
                        >
                          Google Play Store
                        </a>
                        .
                      </Text>
                      <Flex maxWidth={'max-content'} gap={0}>
                        <a
                          href='https://agoralivestock.app.link/marketplaceapplink'
                          target='_blank'
                          rel='noopener noreferrer'
                          style={{ color: globalTheme.colors.blue }}
                        >
                          <Image
                            className='image-wrapper'
                            src={MarketplaceBannerGif}
                            alt='App Store'
                            unoptimized
                          />
                        </a>
                      </Flex>
                    </Flex>
                    <BuyOrderCloseWrapper
                      onClick={() => {
                        hideBuyOrderNoticeHandler();
                      }}
                    >
                      <Icon component='CancelIcon' size={1} />
                    </BuyOrderCloseWrapper>
                  </BuyListingNoticeWrapper>
                  {isFetching && !isFetchingNextPage && (
                    <Flex
                      justifyContent='center'
                      alignItems='center'
                      padding={1}
                      direction='column'
                      style={{
                        transition: 'all 0.2s',
                        pointerEvents: 'none',
                      }}
                    >
                      <Loader size={3} />
                      <Text maxWidth={'max-content'} textAlign='center'>
                        Loading...
                      </Text>
                    </Flex>
                  )}
                  <ListingsCardContainer
                    style={{
                      opacity: isFetching && !isFetchingNextPage ? 0.3 : 1,
                      transition: 'opacity 0.2s',
                      pointerEvents:
                        isFetching && !isFetchingNextPage ? 'none' : 'auto',
                    }}
                  >
                    {listingData &&
                      listingData.map((data: ListingRead) => (
                        <PublicListingsCard
                          key={data.id}
                          data={data}
                          route={route}
                          isLoading={isRefetching}
                          refetch={refetch}
                        />
                      ))}

                    {queryLength <= 0 &&
                      listingData &&
                      listingData.length === 0 && (
                        <>No Data available at the moment</>
                      )}
                  </ListingsCardContainer>

                  {queryLength > 0 &&
                    data &&
                    data?.pages[0]?.results &&
                    data?.pages[0]?.results.length === 0 && (
                      <NoResult>
                        Your search returned no results. Try adjusting your
                        criteria.
                      </NoResult>
                    )}
                </Spacer>

                {hasNextPage && (
                  <LoadMoreBtn>
                    <Button
                      size='small'
                      onClick={() => fetchNextPage()}
                      isLoading={isFetchingNextPage}
                      disabled={!hasNextPage}
                    >
                      Load More
                    </Button>
                  </LoadMoreBtn>
                )}
              </>
            )}
          </ListWrapper>
        </>
      )}

      {!listingsLoading && tableView && (
        <>
          {session && (
            <ToggleContainer>
              <Toggle
                onChange={(e: any) => {
                  router.push({
                    pathname: PATHS.marketplace.listings,
                    search: e.target.checked ? 'networks=all' : '',
                  });
                }}
                backgroundColor='darkBlue'
                checked={router.query?.networks === 'all'}
                toggleOnContent={'Networks Only'}
                toggleOffContent={'All listings'}
              />
            </ToggleContainer>
          )}
          <MarketplaceTableView listingData={listingData} refetch={refetch} />
        </>
      )}
      {!listingsLoading && !hideFooter && <AgoraAdvertisement />}
      {!listingsLoading && !hideFooter && <MarketplaceFooter />}
      <CreateListingDialog
        isOpen={createListingDialogOpen}
        onDismiss={closeCreateListingDialog}
      />
    </ListingWrapper>
  );
};
